// Task rows shared by the job cards and the To do tab: reminders the tracker raises, and tasks people add.
import type { AutoTask, Item, Task } from '@shared/index';
import { autoTasks, fmtDate, lateTask, money, stageView } from '@shared/index';
import { store, today, ui } from '../store';
import { addTask, deleteTask, saveTask } from '../actions';
import { openJob } from '../nav';
import { el, twoTap } from '../ui';

/** A row in a task list: an automatic reminder, or a task someone added. */
export type Row = AutoTask | (Task & { auto?: false });

/** Overdue, for a task someone added. Reminders are never "late"; their urgency is their level. */
export const isLate = (t: Row, now: string): boolean => !t.auto && lateTask(t, now);

/** "Q17011 · 12 Smith St · Bobby", linking to the job (or the job a stage belongs to). */
export function jobRef(quote: string | null, pm: string): HTMLElement {
  const stg = quote ? store.stages.find((x) => x.id === quote) : undefined;
  const j = stg ? stageView(stg, store.jobs.find((x) => x.quote === stg.parent_quote)) : quote ? store.jobs.find((x) => x.quote === quote) : undefined;
  const sm = el('small');
  if (quote) {
    const a = el('a', undefined, stg ? stg.parent_quote : quote);
    a.href = '#jobs';
    a.addEventListener('click', (e) => { e.preventDefault(); openJob(quote); });
    sm.append(a, j ? ' · ' + j.address : '');
  }
  if (pm) sm.append((quote ? ' · ' : '') + pm);
  return sm;
}

export function taskRow(t: Row, opts: { inJob?: boolean } = {}): HTMLElement {
  const now = today();
  const late = isLate(t, now);
  const done = !t.auto && t.done;
  const row = el('div', 'ti' + ((t.auto && t.level === 'urgent') || late ? ' urgent' : '') + (done ? ' done' : ''));
  if (t.auto) row.append(el('span', 'dot'));
  else {
    const cb = el('input');
    cb.type = 'checkbox'; cb.checked = !!t.done; cb.disabled = !store.canWrite;
    cb.setAttribute('aria-label', 'Done: ' + t.text);
    cb.addEventListener('change', () => saveTask(t.id, { done: cb.checked }));
    row.append(cb);
  }
  const tx = el('div', 'tx');
  tx.append(el('span', undefined, t.text));
  if (!opts.inJob && (t.quote || !ui.tdo.pm)) tx.append(jobRef(t.quote, ui.tdo.pm ? '' : t.pm));
  const side = el('div', 'side');
  if (!t.auto && t.due) side.append(el('span', 'due' + (late ? ' late' : ''), (late ? 'Overdue ' : 'Due ') + fmtDate(t.due)));
  if (!t.auto && store.canWrite) {
    const x = el('button', 'xbtn', '×');
    x.type = 'button'; x.setAttribute('aria-label', 'Delete task');
    twoTap(x, 'Delete?', () => deleteTask(t.id), 2500, (on) => { x.style.fontSize = on ? '0.75rem' : ''; });
    side.append(x);
  }
  row.append(tx, side);
  return row;
}

/** One job in a "next steps" group: quote · address · PM (or, filtered to one PM, $ left or the install date). */
export function stageRow(t: AutoTask): HTMLElement {
  const row = el('div', 'srow');
  const a = el('a', undefined, t.quote);
  a.href = '#jobs';
  a.addEventListener('click', (e) => { e.preventDefault(); openJob(t.quote); });
  const pm = ui.tdo.pm ? (t.stage === 'crane' ? fmtDate(t.job.install_date) : money(t.job.remaining)) : t.pm;
  row.append(a, el('span', 'ad', t.job.address), el('span', 'pmt', pm));
  return row;
}

/** Who a task added from a card is filed under: the job's PM, or the signed-in PM when the job's isn't on the PM list
 * (a sheet name the tracker doesn't know, or a stage whose parent job is gone). tasks.pm must name a real PM. */
function taskPm(j: Item): string | null {
  if (store.pms.some((p) => p.name === j.pm)) return j.pm;
  const mine = store.profile?.pm_name;
  return mine && store.pms.some((p) => p.name === mine) ? mine : null;
}

/** The "To do for this job" box inside a card: its reminders, its open tasks, and a quick add. */
export function jobTasks(j: Item): HTMLElement {
  const box = el('div', 'jtasks');
  box.append(el('div', 'lbl', 'To do for this job'));
  const items: Row[] = [...autoTasks(j, today()), ...store.tasks.filter((t) => t.quote === j.quote && !t.done)];
  if (!items.length) box.append(el('div', 'note-line', 'Nothing outstanding.'));
  items.forEach((t) => box.append(taskRow(t, { inJob: true })));
  const pm = taskPm(j);
  if (store.canWrite && !pm) box.append(el('div', 'note-line', 'Give this job a PM to add tasks to it.'));
  if (store.canWrite && pm) {
    const ar = el('div', 'addrow');
    const inp = el('input');
    inp.type = 'text'; inp.placeholder = 'Add a task for this job'; inp.id = 'add-' + j.quote;
    inp.setAttribute('aria-label', 'Add a task for ' + j.quote);
    const b = el('button', 'btn', 'Add');
    b.type = 'button';
    const go = () => {
      const v = inp.value.trim();
      if (!v) return;
      inp.value = ''; inp.blur();
      addTask({ text: v, pm, quote: j.quote, due: null });
    };
    b.addEventListener('click', go);
    inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } });
    ar.append(inp, b);
    box.append(ar);
  }
  return box;
}
